/**
 * The one place that decides which backend a session talks to. Two worlds,
 * never mixed:
 *
 *  - a real session (token from the e-mail code)
 *    gets the API adapter and nothing else — it never reads demo data;
 *  - the demo (marketing, /demo…) gets the in-browser store, which has no
 *    network access at all.
 *
 * Signed out, only the anonymous club list of the login page is needed; that
 * is the API's as well. Both backends re-check the session mode on every call,
 * so a stale reference after a switch fails instead of crossing over.
 */
import { createApiBackend, createDemoBackend, type Backend, type DemoStorage, type Me } from '@fsv/data';
import { api } from '../api';
import { sessionStore } from '../auth/sessionStore';
import { loadDrawingsForType } from '../drawings/loadDrawings';
import { downscalePng } from '../lib/drawingFile';

/** Fired on window when the demo state no longer fits localStorage; AppShell shows a hint. */
export const STORAGE_FULL_EVENT = 'fsv:storage-full';

function browserStorage(): DemoStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export type BackendMode = 'api' | 'demo' | 'signed-out';

function sessionMode(): BackendMode {
  const s = sessionStore.get();
  return s.status === 'signed-in' ? s.session.mode : 'signed-out';
}

/** The demo actor, but only while the session is a demo session. */
function demoActor(): Me | null {
  const s = sessionStore.get();
  return s.status === 'signed-in' && s.session.mode === 'demo' ? s.session.me : null;
}

export function createBackendFor(mode: BackendMode): Backend {
  if (mode === 'demo') {
    return createDemoBackend({
      getActor: demoActor,
      storage: browserStorage(),
      loadDrawings: loadDrawingsForType,
      onStorageFull: () => window.dispatchEvent(new Event(STORAGE_FULL_EVENT)),
      // Uploaded drawings are kept as ≤ 800-px copies; localStorage holds no originals.
      prepareDrawing: (blob) => downscalePng(blob),
    });
  }
  return createApiBackend({ client: api, isRealSession: () => sessionMode() === 'api' });
}
